/**
 * Canonical list of required NuGet packages per project layer.
 *
 * Used by:
 * - `init` command: to install packages during project creation
 * - `upgrade` command: to reconcile missing packages in older projects
 *
 * SmartStack is NOT in this list because it has separate
 * version-pinning logic (--version, --prerelease).
 *
 * ALL packages in this list are installed WITHOUT --prerelease
 * (Microsoft packages must always use stable versions matching
 * the project's target .NET framework).
 */

export interface RequiredPackageSet {
  /** Project suffix: 'Domain', 'Infrastructure', or 'Api' */
  suffix: string;
  /** NuGet package names that must be present */
  packages: string[];
}

/**
 * NuGet package "families" whose satellite packages carry an EXACT (`=`) dependency
 * on a shared core package — e.g. `Hangfire.AspNetCore 1.8.23` locks
 * `Hangfire.NetCore = 1.8.23`. When `upgrade` reconciles a missing satellite from
 * such a family, it MUST pin the added package to the version already present in the
 * .csproj; otherwise `dotnet add` resolves the floating latest, whose locked core
 * dependency outranks the project's existing direct reference and NuGet fails the
 * restore with NU1605 (package downgrade detected). Each entry is a name prefix.
 */
export const LOCKED_PACKAGE_FAMILIES: string[] = ['Hangfire.'];

interface ExistingPackage {
  name: string;
  currentVersion: string;
}

/**
 * Resolves the version a soon-to-be-added required package should be pinned to.
 *
 * If `packageName` belongs to a locked family (see {@link LOCKED_PACKAGE_FAMILIES})
 * AND the project already references one or more *other* members of that family,
 * returns the LOWEST concrete version among them — the safe floor so the new
 * package's exact core dependency never outranks an existing direct reference
 * (the NU1605 trigger).
 *
 * Returns `null` when the package is outside every locked family, or when no sibling
 * of the family is present yet. In the latter case the floating latest is safe: a
 * fresh `init` installs the whole family together at a mutually-consistent version.
 */
export function resolveLockedFamilyVersion(
  packageName: string,
  existingPackages: ExistingPackage[],
): string | null {
  const family = LOCKED_PACKAGE_FAMILIES.find((prefix) =>
    packageName.toLowerCase().startsWith(prefix.toLowerCase()),
  );
  if (!family) return null;

  const siblingVersions = existingPackages
    .filter(
      (p) =>
        p.name.toLowerCase().startsWith(family.toLowerCase()) &&
        p.name.toLowerCase() !== packageName.toLowerCase() &&
        isConcreteVersion(p.currentVersion),
    )
    .map((p) => p.currentVersion.trim());

  if (siblingVersions.length === 0) return null;

  return siblingVersions.sort(compareNuGetVersions)[0];
}

/** True for a fully concrete version like `1.8.21` or `1.8.21.0` (not `*`, a range or empty). */
function isConcreteVersion(version: string): boolean {
  return /^\d+(\.\d+){1,3}(-[0-9A-Za-z.-]+)?$/.test(version.trim());
}

/**
 * Minimal NuGet/semver comparator over up-to-4 numeric parts.
 * A release sorts ABOVE its prerelease (1.0.0 > 1.0.0-rc); two prereleases compare
 * lexicographically. Sufficient for picking the lowest version of a package family
 * (here) and the highest required floor of an NU1605 conflict (nuget-downgrade.ts).
 */
export function compareNuGetVersions(a: string, b: string): number {
  const [coreA, preA] = a.split('-');
  const [coreB, preB] = b.split('-');
  const partsA = coreA.split('.').map((n) => parseInt(n, 10) || 0);
  const partsB = coreB.split('.').map((n) => parseInt(n, 10) || 0);

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const diff = (partsA[i] || 0) - (partsB[i] || 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }

  if (!preA && !preB) return 0;
  if (!preA) return 1; // release > prerelease
  if (!preB) return -1;
  return preA < preB ? -1 : preA > preB ? 1 : 0;
}

export const REQUIRED_PACKAGES: RequiredPackageSet[] = [
  {
    suffix: 'Domain',
    packages: [],
  },
  {
    suffix: 'Infrastructure',
    packages: [
      'Microsoft.EntityFrameworkCore',
      'Microsoft.EntityFrameworkCore.Design',
      'Microsoft.EntityFrameworkCore.SqlServer',
    ],
  },
  {
    suffix: 'Api',
    packages: [
      'Microsoft.AspNetCore.OpenApi',
      'Microsoft.AspNetCore.Authentication.JwtBearer',
      'Microsoft.EntityFrameworkCore.Design',
      'Swashbuckle.AspNetCore',
      'Microsoft.AspNetCore.SignalR.StackExchangeRedis',
      'Azure.Monitor.OpenTelemetry.AspNetCore',
      // R6 — Hangfire (durable workflow resume). Not transitive from the SmartStack
      // umbrella package because its sub-projects use PrivateAssets="all". Required
      // at runtime by HangfireWorkflowResumeScheduler (registered inside AddSmartStack).
      'Hangfire.AspNetCore',
      'Hangfire.SqlServer',
    ],
  },
];
